import React, { useState, useEffect, useMemo } from 'react';

function Broadcast() {
    const [templates, setTemplates] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [selectedTemplateObj, setSelectedTemplateObj] = useState(null);
    const [params, setParams] = useState([]);
    const [selectedContacts, setSelectedContacts] = useState([]);
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);
    const [msg, setMsg] = useState(null);
    const [loading, setLoading] = useState(true);
    const [step, setStep] = useState(1);

    // Advanced contact selection state
    const [contactSearch, setContactSearch] = useState('');
    const [activeTags, setActiveTags] = useState([]); // multi-tag filter
    const [tagMode, setTagMode] = useState('OR'); // OR = any tag, AND = all tags
    const [showActiveOnly, setShowActiveOnly] = useState(false);

    const API = process.env.REACT_APP_WHATSAPP_API || 'https://whatsapp.socialpartners.in/api';

    useEffect(() => {
        fetchTemplates();
        fetchContacts();
    }, []);

    const showMsg = (text, type = 'success') => {
        setMsg({ text, type });
        setTimeout(() => setMsg(null), 4000);
    };

    const fetchTemplates = async () => {
        try {
            const res = await fetch(`${API}/templates`);
            const data = await res.json();
            const list = Array.isArray(data) ? data : data.data || [];
            setTemplates(list.filter(t => t.status === 'APPROVED'));
        } catch (err) {
            showMsg('Failed to load templates', 'error');
        }
    };

    const fetchContacts = async () => {
        try {
            setLoading(true);
            const res = await fetch(`${API}/wa-contacts`);
            const data = await res.json();
            setContacts(data.data || []);
        } catch (err) {
            showMsg('Failed to load contacts', 'error');
        } finally {
            setLoading(false);
        }
    };

    const onTemplateChange = (e) => {
        const name = e.target.value;
        setSelectedTemplate(name);
        const template = templates.find(t => t.name === name);
        setSelectedTemplateObj(template || null);
        if (template) {
            const body = (template.components || []).find(c => c.type === 'BODY');
            const bodyText = body?.text || '';
            const count = (bodyText.match(/\{\{(\d+)\}\}/g) || []).length;
            setParams(Array(count).fill(''));
        } else {
            setParams([]);
        }
    };

    // All tags across all contacts
    const allTags = useMemo(() => [
        ...new Set(
            contacts.flatMap(c => (c.tags || '').split(',').map(t => t.trim()).filter(Boolean))
        )
    ], [contacts]);

    // Filtered contacts based on search + active tags + active-only
    const filteredContacts = useMemo(() => {
        return contacts.filter(c => {
            // Search filter
            if (contactSearch) {
                const q = contactSearch.toLowerCase();
                const nameMatch = (c.name || '').toLowerCase().includes(q);
                const phoneMatch = (c.phone || '').includes(q);
                if (!nameMatch && !phoneMatch) return false;
            }

            // Active only filter
            if (showActiveOnly && !c.isActive) return false;

            // Tag filter
            if (activeTags.length > 0) {
                const contactTags = (c.tags || '').split(',').map(t => t.trim()).filter(Boolean);
                if (tagMode === 'OR') {
                    // Contact must have at least one of the active tags
                    if (!activeTags.some(t => contactTags.includes(t))) return false;
                } else {
                    // AND: Contact must have ALL active tags
                    if (!activeTags.every(t => contactTags.includes(t))) return false;
                }
            }

            return true;
        });
    }, [contacts, contactSearch, activeTags, tagMode, showActiveOnly]);

    const toggleContact = (phone) => {
        setSelectedContacts(prev =>
            prev.includes(phone) ? prev.filter(p => p !== phone) : [...prev, phone]
        );
    };

    const toggleSelectAllFiltered = () => {
        const filteredPhones = filteredContacts.map(c => c.phone);
        const allFilteredSelected = filteredPhones.every(p => selectedContacts.includes(p));
        if (allFilteredSelected) {
            // Deselect only filtered contacts
            setSelectedContacts(prev => prev.filter(p => !filteredPhones.includes(p)));
        } else {
            // Add all filtered contacts to selection
            setSelectedContacts(prev => [...new Set([...prev, ...filteredPhones])]);
        }
    };

    const selectByTag = (tag) => {
        const tagContacts = contacts
            .filter(c => (c.tags || '').split(',').map(t => t.trim()).includes(tag))
            .map(c => c.phone);
        setSelectedContacts(prev => [...new Set([...prev, ...tagContacts])]);
    };

    const deselectByTag = (tag) => {
        const tagContacts = contacts
            .filter(c => (c.tags || '').split(',').map(t => t.trim()).includes(tag))
            .map(c => c.phone);
        setSelectedContacts(prev => prev.filter(p => !tagContacts.includes(p)));
    };

    const toggleActiveTag = (tag) => {
        setActiveTags(prev =>
            prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
        );
    };

    const clearAll = () => {
        setSelectedContacts([]);
        setActiveTags([]);
        setContactSearch('');
        setShowActiveOnly(false);
    };

    const getTagContactCount = (tag) => {
        return contacts.filter(c =>
            (c.tags || '').split(',').map(t => t.trim()).includes(tag)
        ).length;
    };

    const getSelectedByTag = (tag) => {
        const tagPhones = contacts
            .filter(c => (c.tags || '').split(',').map(t => t.trim()).includes(tag))
            .map(c => c.phone);
        return tagPhones.filter(p => selectedContacts.includes(p)).length;
    };

    const allFilteredSelected = filteredContacts.length > 0 &&
        filteredContacts.every(c => selectedContacts.includes(c.phone));

    const getBodyText = () => {
        if (!selectedTemplateObj) return '';
        const body = (selectedTemplateObj.components || []).find(c => c.type === 'BODY');
        return body?.text || '';
    };

    const getPreviewText = () => {
        let text = getBodyText();
        params.forEach((p, i) => {
            text = text.replace(new RegExp(`\\{\\{${i + 1}\\}\\}`, 'g'), p || `{{${i + 1}}}`);
        });
        return text;
    };

    const sendBroadcast = async () => {
        if (!selectedTemplate) { showMsg('Select a template', 'error'); return; }
        if (selectedContacts.length === 0) { showMsg('Select at least one contact', 'error'); return; }
        if (!window.confirm(`Send to ${selectedContacts.length} contacts?`)) return;

        setSending(true);
        setResult(null);

        try {
            const contactIds = contacts
                .filter(c => selectedContacts.includes(c.phone))
                .map(c => c.id);

            const payload = {
                templateName: selectedTemplate,
                parameters: params,
                contactIds,
                ...(selectedTemplateObj?.headerMediaId
                    ? { headerMediaId: selectedTemplateObj.headerMediaId }
                    : {}),
            };

            const res = await fetch(`${API}/broadcast`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Broadcast failed');

            setResult(data);
            setStep(1);
            setSelectedContacts([]);
            setSelectedTemplate('');
            setSelectedTemplateObj(null);
            setParams([]);
            clearAll();
            showMsg(`Sent to ${data.sent} contacts, ${data.failed} failed`);
        } catch (err) {
            showMsg(err.message, 'error');
        } finally {
            setSending(false);
        }
    };

    const getInitials = (name) => {
        if (!name) return '?';
        const str = String(name);
        if (/^\d+$/.test(str)) return str.slice(-2);
        return str.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
    };

    return (
        <div className="page-wrapper">
            <div className="content container-fluid">

                {msg && (
                    <div style={{
                        position: 'fixed', top: 20, right: 20, zIndex: 9999,
                        background: msg.type === 'error' ? '#ef4444' : '#22c55e',
                        color: '#fff', padding: '10px 20px', borderRadius: 8,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)', fontSize: 14,
                    }}>
                        {msg.text}
                    </div>
                )}

                <div className="page-header">
                    <div className="row align-items-center">
                        <div className="col">
                            <h3 className="page-title">Broadcast</h3>
                            <ul className="breadcrumb">
                                <li className="breadcrumb-item"><a href="/dashboard">Dashboard</a></li>
                                <li className="breadcrumb-item active">Broadcast</li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Steps */}
                <div style={{ display: 'flex', marginBottom: 28, background: '#fff', borderRadius: 10, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
                    {[{ n: 1, label: 'Select Template' }, { n: 2, label: 'Select Contacts' }, { n: 3, label: 'Review & Send' }].map(s => (
                        <div
                            key={s.n}
                            onClick={() => step > s.n && setStep(s.n)}
                            style={{
                                flex: 1, padding: '14px 20px', textAlign: 'center',
                                background: step === s.n ? '#128C7E' : step > s.n ? '#e8f5e9' : '#fff',
                                color: step === s.n ? '#fff' : step > s.n ? '#128C7E' : '#999',
                                fontWeight: step === s.n ? 600 : 400, fontSize: 14,
                                cursor: step > s.n ? 'pointer' : 'default',
                                borderRight: s.n < 3 ? '1px solid #e0e0e0' : 'none',
                            }}
                        >
                            <span style={{
                                display: 'inline-block', width: 22, height: 22, borderRadius: '50%',
                                lineHeight: '22px', fontSize: 12, marginRight: 8, fontWeight: 600,
                                background: step === s.n ? 'rgba(255,255,255,0.3)' : step > s.n ? '#128C7E' : '#e0e0e0',
                                color: step >= s.n ? '#fff' : '#999',
                            }}>
                                {step > s.n ? '✓' : s.n}
                            </span>
                            {s.label}
                        </div>
                    ))}
                </div>

                <div className="row">
                    <div className="col-md-8">

                        {/* Step 1 — Template */}
                        {step === 1 && (
                            <div className="card">
                                <div className="card-header"><h5 className="card-title mb-0">Select Template</h5></div>
                                <div className="card-body">
                                    {templates.length === 0 ? (
                                        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                                            No approved templates found. <a href="/templates">Sync templates first →</a>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="form-group">
                                                <label style={{ fontWeight: 500 }}>Template</label>
                                                <select className="form-control" value={selectedTemplate} onChange={onTemplateChange}>
                                                    <option value="">— Select a template —</option>
                                                    {templates.map(t => (
                                                        <option key={t.id} value={t.name}>
                                                            {t.name} ({t.language} · {t.category})
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            {params.length > 0 && (
                                                <div className="form-group">
                                                    <label style={{ fontWeight: 500 }}>Fill Parameters</label>
                                                    {params.map((p, i) => (
                                                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                                                            <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#999', width: 36, flexShrink: 0 }}>
                                                                {`{{${i + 1}}}`}
                                                            </span>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                placeholder={`Value for {{${i + 1}}}`}
                                                                value={p}
                                                                onChange={e => {
                                                                    const np = [...params];
                                                                    np[i] = e.target.value;
                                                                    setParams(np);
                                                                }}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            <button
                                                className="btn btn-primary"
                                                disabled={!selectedTemplate}
                                                onClick={() => setStep(2)}
                                            >
                                                Next: Select Contacts →
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Step 2 — Advanced Contacts */}
                        {step === 2 && (
                            <div className="card">
                                <div className="card-header">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <h5 className="card-title mb-0">Select Contacts</h5>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                            {selectedContacts.length > 0 && (
                                                <button
                                                    onClick={() => setSelectedContacts([])}
                                                    style={{
                                                        background: 'none', border: 'none', cursor: 'pointer',
                                                        fontSize: 12, color: '#999', padding: 0,
                                                    }}
                                                >
                                                    Clear all
                                                </button>
                                            )}
                                            <span style={{
                                                background: '#128C7E', color: '#fff',
                                                padding: '3px 12px', borderRadius: 12, fontSize: 13, fontWeight: 600,
                                            }}>
                                                {selectedContacts.length} selected
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="card-body">

                                    {/* Tag-based quick actions */}
                                    {allTags.length > 0 && (
                                        <div style={{
                                            background: '#f8f9fa', borderRadius: 10, padding: 16, marginBottom: 16,
                                            border: '1px solid #e8e8e8',
                                        }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                                <span style={{ fontSize: 12, fontWeight: 600, color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                    Tags
                                                </span>
                                                <div style={{ display: 'flex', gap: 4 }}>
                                                    <button
                                                        onClick={() => setTagMode('OR')}
                                                        style={{
                                                            padding: '2px 10px', borderRadius: 4, fontSize: 11,
                                                            border: '1px solid',
                                                            borderColor: tagMode === 'OR' ? '#128C7E' : '#e0e0e0',
                                                            background: tagMode === 'OR' ? '#128C7E' : '#fff',
                                                            color: tagMode === 'OR' ? '#fff' : '#999',
                                                            cursor: 'pointer',
                                                        }}
                                                    >
                                                        ANY
                                                    </button>
                                                    <button
                                                        onClick={() => setTagMode('AND')}
                                                        style={{
                                                            padding: '2px 10px', borderRadius: 4, fontSize: 11,
                                                            border: '1px solid',
                                                            borderColor: tagMode === 'AND' ? '#128C7E' : '#e0e0e0',
                                                            background: tagMode === 'AND' ? '#128C7E' : '#fff',
                                                            color: tagMode === 'AND' ? '#fff' : '#999',
                                                            cursor: 'pointer',
                                                        }}
                                                    >
                                                        ALL
                                                    </button>
                                                </div>
                                            </div>

                                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                                {allTags.map(tag => {
                                                    const total = getTagContactCount(tag);
                                                    const selected = getSelectedByTag(tag);
                                                    const isFilterActive = activeTags.includes(tag);
                                                    return (
                                                        <div key={tag} style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                                                            <button
                                                                onClick={() => toggleActiveTag(tag)}
                                                                style={{
                                                                    padding: '4px 10px 4px 12px', fontSize: 12,
                                                                    border: '1.5px solid',
                                                                    borderRight: 'none',
                                                                    borderRadius: '20px 0 0 20px',
                                                                    borderColor: isFilterActive ? '#128C7E' : '#e0e0e0',
                                                                    background: isFilterActive ? '#e8f5e9' : '#fff',
                                                                    color: isFilterActive ? '#128C7E' : '#666',
                                                                    cursor: 'pointer',
                                                                    fontWeight: isFilterActive ? 600 : 400,
                                                                }}
                                                                title={`Filter by ${tag}`}
                                                            >
                                                                {isFilterActive ? '✓ ' : ''}{tag}
                                                                <span style={{ marginLeft: 4, fontSize: 10, color: '#999' }}>
                                                                    {selected > 0 ? `${selected}/` : ''}{total}
                                                                </span>
                                                            </button>
                                                            <button
                                                                onClick={() => selectByTag(tag)}
                                                                title={`Select all ${tag}`}
                                                                style={{
                                                                    padding: '4px 6px', fontSize: 11,
                                                                    border: '1.5px solid',
                                                                    borderRight: 'none',
                                                                    borderLeft: '1px solid #e0e0e0',
                                                                    borderColor: '#e0e0e0',
                                                                    background: '#fff',
                                                                    color: '#128C7E',
                                                                    cursor: 'pointer',
                                                                }}
                                                            >
                                                                +
                                                            </button>
                                                            <button
                                                                onClick={() => deselectByTag(tag)}
                                                                title={`Deselect all ${tag}`}
                                                                style={{
                                                                    padding: '4px 6px', fontSize: 11,
                                                                    border: '1.5px solid',
                                                                    borderColor: '#e0e0e0',
                                                                    borderRadius: '0 20px 20px 0',
                                                                    background: '#fff',
                                                                    color: '#ef4444',
                                                                    cursor: 'pointer',
                                                                }}
                                                            >
                                                                −
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            {activeTags.length > 0 && (
                                                <div style={{ marginTop: 8, fontSize: 12, color: '#128C7E' }}>
                                                    Filtering: contacts with <b>{tagMode === 'OR' ? 'any' : 'all'}</b> of:{' '}
                                                    {activeTags.map(t => <b key={t}>{t}</b>).reduce((a, b) => [a, ', ', b])}
                                                    <button
                                                        onClick={() => setActiveTags([])}
                                                        style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer', color: '#999', fontSize: 11 }}
                                                    >
                                                        ✕ clear filter
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Search + active only */}
                                    <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                                        <div style={{ flex: 1, position: 'relative' }}>
                                            <i className="fas fa-search" style={{
                                                position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                                                color: '#999', fontSize: 13,
                                            }}></i>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Search by name or phone..."
                                                value={contactSearch}
                                                onChange={e => setContactSearch(e.target.value)}
                                                style={{ paddingLeft: 32 }}
                                            />
                                        </div>
                                        <label style={{
                                            display: 'flex', alignItems: 'center', gap: 6, fontSize: 13,
                                            color: '#555', cursor: 'pointer', whiteSpace: 'nowrap',
                                            padding: '0 12px', background: showActiveOnly ? '#e8f5e9' : '#f8f9fa',
                                            borderRadius: 8, border: '1px solid', borderColor: showActiveOnly ? '#128C7E' : '#e0e0e0',
                                        }}>
                                            <input
                                                type="checkbox"
                                                checked={showActiveOnly}
                                                onChange={e => setShowActiveOnly(e.target.checked)}
                                            />
                                            Active only
                                        </label>
                                    </div>

                                    {/* Select all filtered */}
                                    <div style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        padding: '8px 12px', borderRadius: 8,
                                        background: '#f8f9fa', marginBottom: 8,
                                        border: '1px solid #e8e8e8',
                                    }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0, cursor: 'pointer', fontSize: 13 }}>
                                            <input
                                                type="checkbox"
                                                checked={allFilteredSelected}
                                                onChange={toggleSelectAllFiltered}
                                            />
                                            {activeTags.length > 0 || contactSearch || showActiveOnly
                                                ? `Select all filtered (${filteredContacts.length})`
                                                : `Select all (${contacts.length} contacts)`
                                            }
                                        </label>
                                        <span style={{ fontSize: 12, color: '#999' }}>
                                            Showing {filteredContacts.length} of {contacts.length}
                                        </span>
                                    </div>

                                    {/* Contact list */}
                                    {loading ? (
                                        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Loading contacts...</div>
                                    ) : filteredContacts.length === 0 ? (
                                        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                                            {contacts.length === 0
                                                ? <><a href="/contacts">Add contacts first →</a></>
                                                : 'No contacts match current filters'
                                            }
                                        </div>
                                    ) : (
                                        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                                            {filteredContacts.map(c => (
                                                <div
                                                    key={c.id}
                                                    onClick={() => toggleContact(c.phone)}
                                                    style={{
                                                        display: 'flex', alignItems: 'center', gap: 10,
                                                        padding: '10px 12px', cursor: 'pointer', borderRadius: 8, marginBottom: 4,
                                                        background: selectedContacts.includes(c.phone) ? '#f0faf8' : '#fff',
                                                        border: `1px solid ${selectedContacts.includes(c.phone) ? '#b2dfdb' : '#f0f0f0'}`,
                                                        transition: 'all 0.1s',
                                                        opacity: c.isActive ? 1 : 0.6,
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedContacts.includes(c.phone)}
                                                        onChange={() => toggleContact(c.phone)}
                                                        onClick={e => e.stopPropagation()}
                                                    />
                                                    <div style={{
                                                        width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                                                        background: '#e8f5e9', color: '#1b5e20',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        fontWeight: 600, fontSize: 12,
                                                    }}>
                                                        {getInitials(c.name || c.phone)}
                                                    </div>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontWeight: 500, fontSize: 13 }}>{c.name || c.phone}</div>
                                                        <div style={{ fontSize: 12, color: '#999' }}>{c.phone}</div>
                                                    </div>
                                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                                                        {!c.isActive && (
                                                            <span style={{ fontSize: 10, color: '#dc2626', background: '#fee2e2', padding: '1px 6px', borderRadius: 6 }}>
                                                                Inactive
                                                            </span>
                                                        )}
                                                        {c.tags && (
                                                            <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                                                {c.tags.split(',').slice(0, 2).map((tag, i) => (
                                                                    <span key={i} style={{
                                                                        fontSize: 10, color: '#1565c0',
                                                                        background: activeTags.includes(tag.trim()) ? '#bbdefb' : '#e3f2fd',
                                                                        padding: '1px 6px', borderRadius: 8,
                                                                        fontWeight: activeTags.includes(tag.trim()) ? 600 : 400,
                                                                    }}>
                                                                        {tag.trim()}
                                                                    </span>
                                                                ))}
                                                                {c.tags.split(',').length > 2 && (
                                                                    <span style={{ fontSize: 10, color: '#999' }}>
                                                                        +{c.tags.split(',').length - 2}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                                        <button className="btn btn-outline-secondary" onClick={() => setStep(1)}>← Back</button>
                                        <button
                                            className="btn btn-primary"
                                            disabled={selectedContacts.length === 0}
                                            onClick={() => setStep(3)}
                                        >
                                            Next: Review & Send →
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Step 3 — Confirm */}
                        {step === 3 && (
                            <div className="card">
                                <div className="card-header"><h5 className="card-title mb-0">Review & Send</h5></div>
                                <div className="card-body">
                                    <div style={{ background: '#f9f9f9', borderRadius: 8, padding: 16, marginBottom: 20 }}>
                                        <div style={{ fontSize: 12, color: '#999', marginBottom: 8, fontWeight: 600 }}>SUMMARY</div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                                                <span style={{ color: '#666' }}>Template:</span>
                                                <span style={{ fontWeight: 600, fontFamily: 'monospace' }}>{selectedTemplate}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                                                <span style={{ color: '#666' }}>Recipients:</span>
                                                <span style={{ fontWeight: 600, color: '#128C7E' }}>{selectedContacts.length} contacts</span>
                                            </div>
                                            {params.length > 0 && (
                                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                                                    <span style={{ color: '#666' }}>Parameters:</span>
                                                    <span style={{ fontWeight: 500 }}>{params.join(', ')}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Selected contacts preview */}
                                    <div style={{ marginBottom: 20 }}>
                                        <div style={{ fontSize: 12, color: '#999', marginBottom: 8, fontWeight: 600 }}>RECIPIENTS PREVIEW</div>
                                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                            {contacts
                                                .filter(c => selectedContacts.includes(c.phone))
                                                .slice(0, 10)
                                                .map(c => (
                                                    <div key={c.phone} style={{
                                                        display: 'flex', alignItems: 'center', gap: 6,
                                                        background: '#f0faf8', border: '1px solid #b2dfdb',
                                                        borderRadius: 20, padding: '3px 10px', fontSize: 12,
                                                    }}>
                                                        <div style={{
                                                            width: 20, height: 20, borderRadius: '50%',
                                                            background: '#128C7E', color: '#fff',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            fontSize: 9, fontWeight: 700, flexShrink: 0,
                                                        }}>
                                                            {getInitials(c.name || c.phone)}
                                                        </div>
                                                        {c.name || c.phone}
                                                    </div>
                                                ))
                                            }
                                            {selectedContacts.length > 10 && (
                                                <div style={{
                                                    background: '#f0f0f0', borderRadius: 20, padding: '3px 10px',
                                                    fontSize: 12, color: '#666',
                                                }}>
                                                    +{selectedContacts.length - 10} more
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div style={{ marginBottom: 20 }}>
                                        <div style={{ fontSize: 12, color: '#999', marginBottom: 8, fontWeight: 600 }}>MESSAGE PREVIEW</div>
                                        <div style={{
                                            background: '#dcf8c6', borderRadius: '8px 0 8px 8px',
                                            padding: '10px 14px', fontSize: 13, lineHeight: 1.6,
                                            maxWidth: '80%', marginLeft: 'auto',
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                        }}>
                                            {getPreviewText() || 'No preview available'}
                                        </div>
                                    </div>

                                    <div style={{
                                        background: '#fffbeb', border: '1px solid #fed7aa',
                                        borderRadius: 8, padding: '10px 14px', marginBottom: 20,
                                        fontSize: 13, color: '#92400e',
                                    }}>
                                        ⚠️ This will send a WhatsApp message to <strong>{selectedContacts.length} contacts</strong>. This action cannot be undone.
                                    </div>

                                    <div style={{ display: 'flex', gap: 10 }}>
                                        <button className="btn btn-outline-secondary" onClick={() => setStep(2)}>← Back</button>
                                        <button
                                            className="btn btn-success"
                                            onClick={sendBroadcast}
                                            disabled={sending}
                                            style={{ minWidth: 140 }}
                                        >
                                            {sending
                                                ? <span>Sending... <i className="fas fa-spinner fa-spin"></i></span>
                                                : <span><i className="fas fa-paper-plane mr-1"></i> Send to {selectedContacts.length} Contacts</span>
                                            }
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="col-md-4">
                        <div className="card">
                            <div className="card-header"><h5 className="card-title mb-0">Live Preview</h5></div>
                            <div className="card-body">
                                {selectedTemplate ? (
                                    <div>
                                        <div style={{
                                            background: '#e8f5e9', borderRadius: 8, padding: 12,
                                            fontSize: 12, color: '#555', marginBottom: 12, fontFamily: 'monospace',
                                        }}>
                                            {selectedTemplate}
                                        </div>
                                        <div style={{
                                            background: '#dcf8c6', borderRadius: '8px 0 8px 8px',
                                            padding: '10px 14px', fontSize: 13, lineHeight: 1.6,
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                        }}>
                                            {getPreviewText() || <span style={{ color: '#999', fontStyle: 'italic' }}>Fill parameters to preview</span>}
                                        </div>
                                        {selectedContacts.length > 0 && (
                                            <div style={{ marginTop: 16, padding: 10, background: '#f0faf8', borderRadius: 8, fontSize: 13 }}>
                                                <strong style={{ color: '#128C7E' }}>{selectedContacts.length}</strong> contacts selected
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div style={{ textAlign: 'center', padding: 30, color: '#ccc' }}>
                                        <i className="fas fa-paper-plane" style={{ fontSize: 40, marginBottom: 10, display: 'block' }}></i>
                                        Select a template to preview
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Selection stats */}
                        {step === 2 && selectedContacts.length > 0 && (
                            <div className="card mt-3">
                                <div className="card-header"><h5 className="card-title mb-0">Selection Breakdown</h5></div>
                                <div className="card-body" style={{ padding: '12px 16px' }}>
                                    {allTags.map(tag => {
                                        const sel = getSelectedByTag(tag);
                                        const total = getTagContactCount(tag);
                                        if (sel === 0) return null;
                                        return (
                                            <div key={tag} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                                <span style={{
                                                    fontSize: 12, background: '#e3f2fd', color: '#1565c0',
                                                    padding: '2px 8px', borderRadius: 10,
                                                }}>
                                                    {tag}
                                                </span>
                                                <span style={{ fontSize: 12, color: '#555' }}>
                                                    {sel} / {total}
                                                </span>
                                            </div>
                                        );
                                    })}
                                    <div style={{ borderTop: '1px solid #f0f0f0', marginTop: 8, paddingTop: 8, fontSize: 13, fontWeight: 600, color: '#128C7E' }}>
                                        Total: {selectedContacts.length} contacts
                                    </div>
                                </div>
                            </div>
                        )}

                        {result && (
                            <div className="card mt-3">
                                <div className="card-header"><h5 className="card-title mb-0">Last Result</h5></div>
                                <div className="card-body">
                                    <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                                        <div style={{ flex: 1, background: '#dcfce7', borderRadius: 8, padding: '12px 10px', textAlign: 'center' }}>
                                            <div style={{ fontSize: 24, fontWeight: 700, color: '#15803d' }}>{result.sent}</div>
                                            <div style={{ fontSize: 12, color: '#15803d' }}>Sent</div>
                                        </div>
                                        <div style={{ flex: 1, background: '#fee2e2', borderRadius: 8, padding: '12px 10px', textAlign: 'center' }}>
                                            <div style={{ fontSize: 24, fontWeight: 700, color: '#dc2626' }}>{result.failed}</div>
                                            <div style={{ fontSize: 12, color: '#dc2626' }}>Failed</div>
                                        </div>
                                    </div>
                                    {result.results?.failed?.length > 0 && result.results.failed.map((f, i) => (
                                        <div key={i} style={{ fontSize: 12, color: '#dc2626', marginBottom: 4 }}>
                                            {f.name || f.phone}: {f.error}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Broadcast;